import { Component, Input, ChangeDetectionStrategy, inject } from '@angular/core';

import { TranslatePipe } from '@ngx-translate/core';
import { SharedModule } from '../../../@commons/ui-components/shared.module';
import { GardenBedListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/garden-bed-list.response';
import { GardenBedType } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/garden-bed-type.enum';
import { VwPluralPipe } from '../../../@commons/pipes/vw-plural.pipe';
import { StorageService } from '../../../@commons/@services/front/storage.service';
import { formatMetres } from '../../@services/garden-space.util';

interface BedCell {
  occupied: boolean;
}

/** Ligne « planche » compacte dans Mon Potager : mini-aperçu de la grille + nom + résumé. */
@Component({
  selector: 'vw-bed-item',
  imports: [SharedModule, TranslatePipe, VwPluralPipe],
  templateUrl: './bed-item.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './bed-item.component.scss',
})
export class BedItemComponent {
  readonly GardenBedType = GardenBedType;

  @Input() bed!: GardenBedListResponse;
  /** Cultures « riches » (image + nom court) déjà résolues par le parent. */
  @Input() cultures: any[] = [];
  @Input() count = 0;
  /** Nombre total de plants (Σ nbPlants) sur la planche. */
  @Input() plantCount = 0;
  /** Place libre : cases (en cases) ou places (pot), et le total. */
  @Input() freeCells = 0;
  @Input() totalCells = 0;
  /** Positions réellement occupées (x = colonne, y = ligne) — pour colorer les vraies cases. */
  @Input() occupied: { x: number; y: number }[] = [];
  /** Au moins une plante de la planche attend une action (étape manuelle due). */
  @Input() actionNeeded = false;

  /** Plafond de cases rendues dans le mini-aperçu (garde un petit « logo »). */
  private readonly MAX_CELLS = 36;
  private readonly storage = inject(StorageService);

  get isPot(): boolean {
    return this.bed.type === GardenBedType.POT;
  }

  /** « 1,20 × 0,90 m » quand le jardinier a donné ses dimensions, sinon colonnes × lignes. */
  get dimensionLabel(): string {
    if (this.isPot) {
      return 'Pot';
    }
    if (this.bed.widthCm && this.bed.lengthCm) {
      const lang = this.storage.getCurrentLanguage();
      return `${formatMetres(this.bed.widthCm, lang).replace(' m', '')} × ${formatMetres(this.bed.lengthCm, lang)}`;
    }
    return `${this.bed.columns ?? '-'} × ${this.bed.rows ?? '-'}`;
  }

  /** Nombre de colonnes du mini-aperçu. */
  get cols(): number {
    if (this.bed.type === GardenBedType.POT) {
      return Math.min(this.bed.capacity || 1, 4);
    }
    return this.bed.columns || 1;
  }

  /** Largeur (px) du mini-aperçu, bornée pour rester un petit logo. */
  get gridWidthPx(): number {
    return Math.min(56, Math.max(28, this.cols * 14));
  }

  /** Cases du mini-aperçu : reflète les VRAIES positions occupées (planche), sinon premières cases (pot). */
  get cells(): BedCell[] {
    // Pot : pas de coordonnées → on remplit simplement les premières cases.
    if (this.bed.type === GardenBedType.POT) {
      const total = this.bed.capacity || 0;
      const capped = Math.min(total || this.count, this.MAX_CELLS);
      const occ = Math.min(this.count, capped);
      return Array.from({ length: capped }, (_, i) => ({ occupied: i < occ }));
    }
    // Planche rectangulaire : on colore la case (x, y) réellement occupée.
    const cols = this.bed.columns || 1;
    const total = (this.bed.rows || 0) * cols;
    const capped = Math.min(total || this.count, this.MAX_CELLS);
    const occupiedSet = new Set(
      (this.occupied || []).map((p) => p.y * cols + p.x),
    );
    return Array.from({ length: capped }, (_, i) => ({
      occupied: occupiedSet.has(i),
    }));
  }
}
